import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { CVEducationManagerProps, EducationEntry } from './types';
import { validateRequired, validateYearRange, validateGPA } from './utils/validation';
import Input from '../UI/Input';
import Button from '../UI/Button';
import { BiEdit, BiPlus, BiSave, BiTrash } from "react-icons/bi";
import Modal from '../UI/Modal';
import { Edit, Plus, Save, Trash } from "lucide-react";

/**
 * CV Education Manager component
 * Handles CRUD operations for education entries with validation
 */
const CVEducationManager: React.FC<CVEducationManagerProps> = ({ 
  educations, 
  onAdd, 
  onUpdate, 
  onDelete 
}) => {
  const [showModal, setShowModal] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  // Form for adding/editing education entries
  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    watch,
    setValue
  } = useForm<EducationEntry>({
    mode: 'onBlur',
    defaultValues: {
      school_name: '',
      major: '',
      start_year: new Date().getFullYear(),
      end_year: undefined,
      gpa: '',
      relevant_projects: ''
    }
  });

  const watchedStartYear = watch('start_year');
  const watchedEndYear = watch('end_year');

  // Validate education entry data
  const validateEducationEntry = (data: EducationEntry): { isValid: boolean; errors: Record<string, string[]> } => {
    const validationErrors: Record<string, string[]> = {};

    // Required field validation
    validationErrors.school_name = validateRequired(data.school_name, 'School name');
    validationErrors.major = validateRequired(data.major, 'Major');

    // Year validation
    if (!data.start_year) {
      validationErrors.start_year = ['Start year is required'];
    } else {
      const yearErrors = validateYearRange(data.start_year, data.end_year);
      if (yearErrors.length > 0) {
        validationErrors.start_year = yearErrors;
      }
    }

    // GPA validation (optional)
    if (data.gpa) {
      const gpaErrors = validateGPA(data.gpa);
      if (gpaErrors.length > 0) {
        validationErrors.gpa = gpaErrors;
      }
    }

    // Remove empty error arrays
    Object.keys(validationErrors).forEach(key => {
      if (validationErrors[key].length === 0) {
        delete validationErrors[key];
      }
    });

    return {
      isValid: Object.keys(validationErrors).length === 0,
      errors: validationErrors
    };
  };

  // Handle form submission for add/edit
  const onSubmit = async (data: EducationEntry) => {
    setIsSubmitting(true);
    setSubmitError(null);

    try {
      // Validate the form data
      const validationResult = validateEducationEntry(data);
      
      if (!validationResult.isValid) {
        setIsSubmitting(false);
        return;
      }

      // Clean up the data
      const cleanData: EducationEntry = {
        school_name: data.school_name.trim(),
        major: data.major.trim(),
        start_year: Number(data.start_year),
        end_year: data.end_year ? Number(data.end_year) : undefined,
        gpa: data.gpa?.trim() || undefined,
        relevant_projects: data.relevant_projects?.trim() || undefined
      };

      if (editingId !== null) {
        // Update existing entry
        await onUpdate(editingId, cleanData);
        setEditingId(null);
      } else {
        // Add new entry
        await onAdd(cleanData);
      }

      // Reset form and close modal
      reset();
      setShowModal(false);
    } catch (error: any) {
      setSubmitError(error.message || 'Gagal menyimpan data pendidikan. Silakan coba lagi.');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handle edit button click
  const handleEdit = (index: number) => {
    const education = educations[index];
    if (!education.id) {
      setSubmitError('Cannot edit education entry without ID');
      return;
    }
    setEditingId(education.id);
    setShowModal(true);
    
    // Populate form with existing data
    setValue('school_name', education.school_name);
    setValue('major', education.major);
    setValue('start_year', education.start_year);
    setValue('end_year', education.end_year || undefined);
    setValue('gpa', education.gpa || '');
    setValue('relevant_projects', education.relevant_projects || '');
  };

  // Handle delete with confirmation
  const handleDelete = async (index: number) => {
    const education = educations[index];
    if (!education.id) {
      setSubmitError('Cannot delete education entry without ID');
      return;
    }
    
    if (window.confirm('Apakah Anda yakin ingin menghapus data pendidikan ini?')) {
      try {
        await onDelete(education.id);
      } catch (error: any) {
        setSubmitError(error.message || 'Gagal menghapus data pendidikan. Silakan coba lagi.');
      }
    }
  };

  // Handle cancel add/edit
  const handleCancel = () => {
    setShowModal(false);
    setEditingId(null);
    setSubmitError(null);
    reset();
  };

  // Handle add new button
  const handleAddNew = () => {
    setShowModal(true);
    setEditingId(null);
    setSubmitError(null);
    reset();
  };

  return (
    <div className="cv-education-manager">
      <div className="flex flex-wrap">
        <div className="w-full">
          {submitError && (
            <div
              className="p-4 rounded border bg-red-50 border-red-200 text-red-800 mb-3"
              role="alert"
            >
              {submitError}
            </div>
          )}
          {/* Education Entries List */}
          {educations.length > 0 && (
            <div className="education-entries mb-4">
              {educations.map((education, index) => (
                <div
                  key={index}
                  className="bg-white rounded-lg shadow border mb-3"
                >
                  <div className="p-4">
                    <div className="flex flex-col flex-wrap">
                      <div className="md:w-8/12">
                        <h5 className="text-lg mb-2 font-bold">
                          {education.school_name}
                        </h5>
                        <p className="text-gray-700 mb-1">
                          <strong>Jurusan:</strong> {education.major}
                        </p>
                        <p className="text-gray-700 mb-1">
                          <strong>Periode:</strong> {education.start_year}
                          {education.end_year
                            ? ` - ${education.end_year}`
                            : " - Sekarang"}
                        </p>
                        {education.gpa && (
                          <p className="text-gray-700 mb-1">
                            <strong>IPK:</strong> {education.gpa}
                          </p>
                        )}
                        {education.relevant_projects && (
                          <p className="text-gray-700 mb-0">
                            <strong>Proyek Terkait:</strong>{" "}
                            {education.relevant_projects}
                          </p>
                        )}
                      </div>
                      <div className="md:w-4/12 flex items-center justify-end gap-2">
                        <a
                          onClick={() => handleEdit(index)}
                          className="cursor-pointer"
                        >
                          <Edit className="text-primary" size={"1rem"} />
                        </a>
                        <a
                          className="cursor-pointer"
                          onClick={() => handleDelete(index)}
                        >
                          <Trash className="text-red-600" size={"1rem"} />
                        </a>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
          {educations.length > 0 && (
            <div className="flex justify-end items-center mb-4">
              <Button
                primary
                onClick={handleAddNew}
                className="w-full gradient-primary border-0"
              >
                <Plus /> Tambah
              </Button>
            </div>
          )}
          {/* Add/Edit Modal */}
          <Modal show={showModal} onHide={handleCancel} size="lg">
            <Modal.Header closeButton>
              <Modal.Title>
                {editingId !== null
                  ? "Ubah Data Pendidikan"
                  : "Tambah Data Pendidikan Baru"}
              </Modal.Title>
            </Modal.Header>
            <Modal.Body>
              {submitError && (
                <div
                  className="p-4 rounded border bg-red-50 border-red-200 text-red-800 mb-3"
                  role="alert"
                >
                  {submitError}
                </div>
              )}

              <form onSubmit={handleSubmit(onSubmit)} noValidate>
                <div className="flex flex-wrap">
                  {/* School Name */}
                  <div className="w-full md:w-6/12 mb-3">
                    <label htmlFor="school_name" className="form-label">
                      Nama Sekolah/Universitas{" "}
                      <span className="text-red-600">*</span>
                    </label>
                    <Input
                      placeholder="Masukkan nama sekolah/universitas"
                      name="school_name"
                      register={register as any}
                      errors={errors as any}
                      rules={{
                        required: "Nama sekolah wajib diisi",
                        minLength: {
                          value: 2,
                          message: "Nama sekolah minimal 2 karakter",
                        },
                      }}
                      block
                      disabled={isSubmitting}
                    />
                  </div>

                  {/* Major */}
                  <div className="w-full md:w-6/12 mb-3">
                    <label htmlFor="major" className="form-label">
                      Jurusan/Bidang Studi{" "}
                      <span className="text-red-600">*</span>
                    </label>
                    <Input
                      placeholder="Masukkan jurusan atau bidang studi Anda"
                      name="major"
                      register={register as any}
                      errors={errors as any}
                      rules={{
                        required: "Jurusan wajib diisi",
                        minLength: {
                          value: 2,
                          message: "Jurusan minimal 2 karakter",
                        },
                      }}
                      block
                      disabled={isSubmitting}
                    />
                  </div>
                </div>

                <div className="flex flex-wrap">
                  {/* Start Year */}
                  <div className="w-full md:w-4/12 mb-3">
                    <label htmlFor="start_year" className="form-label">
                      Tahun Mulai <span className="text-red-600">*</span>
                    </label>
                    <Input
                      placeholder="2020"
                      type="number"
                      name="start_year"
                      register={register as any}
                      errors={errors as any}
                      rules={{
                        required: "Tahun mulai wajib diisi",
                        min: {
                          value: 1950,
                          message: "Tahun mulai harus setelah 1950",
                        },
                        max: {
                          value: new Date().getFullYear(),
                          message: "Tahun mulai tidak boleh di masa depan",
                        },
                        validate: (value: number) => {
                          if (watchedEndYear && value > watchedEndYear) {
                            return "Tahun mulai harus sebelum tahun selesai";
                          }
                          return true;
                        },
                      }}
                      block
                      disabled={isSubmitting}
                    />
                  </div>

                  {/* End Year */}
                  <div className="w-full md:w-4/12 mb-3">
                    <label htmlFor="end_year" className="form-label">
                      Tahun Selesai{" "}
                      <span className="text-gray-500">
                        (Opsional - kosongkan jika masih berlangsung)
                      </span>
                    </label>
                    <Input
                      placeholder="2024"
                      type="number"
                      name="end_year"
                      register={register as any}
                      errors={errors as any}
                      rules={{
                        min: {
                          value: 1950,
                          message: "Tahun selesai harus setelah 1950",
                        },
                        max: {
                          value: new Date().getFullYear() + 10,
                          message: "Tahun selesai terlalu jauh di masa depan",
                        },
                        validate: (value?: number) => {
                          if (
                            value &&
                            watchedStartYear &&
                            value < watchedStartYear
                          ) {
                            return "Tahun selesai harus setelah tahun mulai";
                          }
                          return true;
                        },
                      }}
                      block
                      disabled={isSubmitting}
                    />
                  </div>

                  {/* GPA */}
                  <div className="w-full md:w-4/12 mb-3">
                    <label htmlFor="gpa" className="form-label">
                      IPK <span className="text-gray-500">(Opsional)</span>
                    </label>
                    <Input
                      placeholder="3.75"
                      name="gpa"
                      register={register as any}
                      errors={errors as any}
                      rules={{
                        pattern: {
                          value: /^\d+(\.\d{1,2})?$/,
                          message: "Masukkan IPK yang valid (contoh: 3.75)",
                        },
                        validate: (value?: string) => {
                          if (value) {
                            const gpaNum = Number.parseFloat(value);
                            if (gpaNum < 0 || gpaNum > 100) {
                              return "IPK harus antara 0 dan 100";
                            }
                          }
                          return true;
                        },
                      }}
                      block
                      disabled={isSubmitting}
                    />
                  </div>
                </div>

                {/* Relevant Projects */}
                <div className="mb-4">
                  <label htmlFor="relevant_projects" className="form-label">
                    Proyek Terkait{" "}
                    <span className="text-gray-500">(Opsional)</span>
                  </label>
                  <textarea
                    className="form-control"
                    placeholder="Jelaskan proyek terkait, skripsi, atau mata kuliah yang relevan"
                    rows={3}
                    {...register("relevant_projects")}
                    disabled={isSubmitting}
                  />
                  {errors.relevant_projects && (
                    <p className="text-red-600 italic mb-2">
                      {errors.relevant_projects.message}
                    </p>
                  )}
                </div>
              </form>
            </Modal.Body>
            <Modal.Footer className="flex justify-end gap-1">
              <Button
                type="button"
                variant="outline"
                onClick={handleCancel}
                disabled={isSubmitting}
                className="px-4"
              >
                Batal
              </Button>
              <Button
                type="submit"
                primary
                disabled={isSubmitting}
                className="px-4 flex items-center gap-2 gradient-primary"
                onClick={handleSubmit(onSubmit)}
              >
                <Save /> {isSubmitting ? "Menyimpan..." : "Simpan Data"}
              </Button>
            </Modal.Footer>
          </Modal>

          {/* Empty State */}
          {educations.length === 0 && (
            <div className="text-center py-5">
              <p className="text-gray-500 mb-3">
                Belum ada data pendidikan yang ditambahkan.
              </p>
              <Button
                primary
                onClick={handleAddNew}
                className="px-4 flex items-center gap-2 gradient-primary mx-auto"
              >
                <Plus /> Tambah Data Pendidikan
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CVEducationManager;